import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CForm, CFormTextarea, CButton, CRow, CCol, CFormCheck, CContainer, CCard, CCardHeader, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import { triggerAlert } from 'components/globals/AlertNotification';
import logo_stream from 'assets/brand/logo_stream.png';
import ToastNotification from "components/globals/ToastNotification";
import SatisfactionSurveyCompleted from './satisfactionSurveyCompleted';

const evaluationOptions = [
    { label: "Muito Insatisfeito", value: 1 },
    { label: "Insatisfeito", value: 2 },
    { label: "Pouco Satisfeito", value: 3 },
    { label: "Satisfeito", value: 4 },
    { label: "Muito Satisfeito", value: 5 },
    { label: "Muitíssimo satisfeito", value: 6 },
    { label: "N.A", value: 'NA' }
];

const evaluationFields = [
    ['information_clarity', 'Qualidade da informação disponibilizada'],
    ['question_resolution', 'Esclarecimento de dúvidas'],
    ['response_time', 'Tempo de resposta a solicitações'],
    ['issue_resolution', 'Eficácia na resolução de problemas'],
    ['integration_capacity', 'Integração de outros serviços'],
    ['condition_compliance', 'Cumprimento das condições'],
    ['competence_level', 'Competência e responsabilidade'],
    ['overall_evaluation', 'Qualidade global do serviço'],
    ['ideal_supplier', 'STREAM está próxima de ser o fornecedor ideal?'],
    ['quality_price_ratio', 'Qualidade do serviço face ao preço'],
    ['error_frequency', 'Houve aspetos que correram mal?'],
    ['problem_management', 'Como foram geridos os problemas?'],
    ['project_delivery', 'Voltaria a entregar projeto à STREAM?'],
    ['recommendation', 'Recomendaria os serviços?']
];

const projectFields = {
    withIncentive: [
        ['with_incentive_energy', 'Energia (Descarbonização, Gases renováveis, etc)'],
        ['with_incentive_idt', 'IDT'],
        ['with_incentive_inov', 'Inovação'],
        ['with_incentive_inter', 'Internacionalização'],
        ['with_incentive_others', 'Outros'],
    ],
    withoutIncentive: [
        ['without_incentive_consulting', 'Consultoria'],
        ['without_incentive_studies', 'Estudos'],
        ['without_incentive_others', 'Outros'],
    ]
};

const renderEvaluationCell = (field, value, formData, setFormData, is_manager) => {
    if (is_manager && formData[field] === null && formData["concluded"]) {
        formData[field] = 'NA'; // Ensure 'N.A' is displayed as 'NA' for managers
    }
    const checked = formData[field] === value;
    return (
        <CTableDataCell
            key={value}
            className="text-center"
            style={{ cursor: is_manager ? 'default' : 'pointer' }}
            onClick={() => {
                if (!is_manager) {
                    setFormData(prev => ({
                        ...prev,
                        [field]: value
                    }));
                }
            }}
        >
            <label style={{ cursor: is_manager ? 'default' : 'pointer' }}>
                <CFormCheck
                    type="radio"
                    name={field}
                    value={value}
                    checked={checked}
                    onChange={() => { }}
                    style={{ pointerEvents: 'none' }}
                    disabled={is_manager}
                />
            </label>
        </CTableDataCell>
    );
};

const SatisfactionSurvey = ({ onSubmit }) => {
    const [formData, setFormData] = useState(null);
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [surveyStatus, setSurveyStatus] = useState(null);
    const [toast, setToast] = useState(null);

    const is_manager = useMemo(() => {
        try {
            const token = localStorage.getItem("access_token");
            return token ? jwtDecode(token).is_manager || false : false;
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const response = await apiClient.get(`/satisfaction-surveys/${uuid}/`, { skipAuth: true });
                const survey = response.data.survey;
                if (survey.concluded && !is_manager) {
                    setSurveyStatus("completed");
                } else {
                    setFormData(survey);
                }
            } catch (error) {
                const status = error?.response?.status;
                if (!is_manager) {
                    navigate("/questionarios-satisfacao");
                    triggerAlert('Questionário não foi encontrado', 'error');
                } else {
                    setSurveyStatus("not_found");
                }
            }
        };
        fetchSurvey();
    }, [uuid, location.pathname, is_manager, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === "radio" ? value : (type === "checkbox" ? checked : value);
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    const calculateFinalScore = (data) => {
        const scores = evaluationFields.map(([field]) => data[field]).filter(val => val !== null && val !== undefined);
        return scores.length > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100 : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const incompleteEvaluations = evaluationFields.some(([field]) =>
            formData[field] === null
        );
        const incentiveKeys = [...projectFields.withIncentive, ...projectFields.withoutIncentive].map(([field]) => field);
        const atLeastOneChecked = incentiveKeys.some((key) => formData[key]);

        if (!atLeastOneChecked) {
            setToast({ title: 'Questionário de satisfação', message: 'Por favor selecione pelo menos uma tipologia de projeto.', type: 'error' });
            return;
        }
        console.log(incompleteEvaluations, formData)
        if (incompleteEvaluations) {
            setToast({ title: 'Questionário de satisfação', message: 'Por favor pontue todas as opções.', type: 'error' });
            return;
        }

        try {
            const cleanedData = { ...formData };
            evaluationFields.forEach(([field]) => {
                if (cleanedData[field] === 'NA') cleanedData[field] = null;
            });
            const final_score = calculateFinalScore(cleanedData);

            await apiClient.put(`/satisfaction-surveys/${uuid}/`, {
                ...cleanedData,
                final_score,
                concluded: true,
            }, { skipAuth: true });

            triggerAlert('Questionário submetido com sucesso!', 'success');
            setSurveyStatus("completed");
        } catch (error) {
            triggerAlert('Erro ao submeter o questionário.', 'error');
            console.error(error);
        }
    };

    if (surveyStatus === "not_found") {
        return (
            <CContainer className="text-center py-5">
                <img src={logo_stream} alt="Logo" style={{ maxWidth: '300px' }} />
                <h4 className="mt-5">O questionário não foi encontrado.</h4>
            </CContainer>
        );
    }

    if (surveyStatus === "completed") {
        return <SatisfactionSurveyCompleted uuid={uuid} />;
    }

    if (!formData) return null;

    return (
        <CContainer>
            <ToastNotification toast={toast} setToast={setToast} />
            <CForm onSubmit={handleSubmit}>
                <CRow className="mb-3">
                    <CCol className="text-center">
                        <img src={logo_stream} alt="Logo" style={{ height: 150, width: 300, objectFit: 'contain' }} />
                    </CCol>
                </CRow>
                <CRow>
                    <CCol className="text-center">
                        <p>A procura pela melhoria contínua dos nossos serviços é um compromisso assumido por nós.<br />Nesse sentido é essencial conhecer a sua opinião.</p>
                    </CCol>
                </CRow>

                {is_manager && (
                    <>
                        <CCard className="mb-3">
                            <CCardHeader>
                                <h5>Cliente</h5>
                            </CCardHeader>
                            <CCardBody>
                                {formData.client_name}
                            </CCardBody>
                        </CCard>
                    </>
                )}

                <CCard className="mb-3">
                    <CCardHeader><h5>Tipologia de Projetos Realizados</h5></CCardHeader>
                    <CCardBody>
                        <CRow className="mb-3">
                            <CCol md={6}>
                                <h6>Projetos com incentivo</h6>
                                {projectFields.withIncentive.map(([field, label]) => (
                                    <label key={field} style={{ display: 'block', cursor: 'pointer' }}>
                                        <CFormCheck
                                            type="checkbox"
                                            name={field}
                                            id={field}
                                            checked={formData[field] || false}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '0.5rem' }}
                                            disabled={is_manager}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </CCol>
                            <CCol md={6}>
                                <h6>Projetos sem incentivo</h6>
                                {projectFields.withoutIncentive.map(([field, label]) => (
                                    <label key={field} style={{ display: 'block', cursor: 'pointer' }}>
                                        <CFormCheck
                                            type="checkbox"
                                            name={field}
                                            id={field}
                                            checked={formData[field] || false}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '0.5rem' }}
                                            disabled={is_manager}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </CCol>
                        </CRow>
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Avaliação dos Serviços</h5></CCardHeader>
                    <CCardBody>
                        <CTable bordered responsive>
                            <CTableHead color="secondary">
                                <CTableRow>
                                    <CTableHeaderCell></CTableHeaderCell>
                                    {evaluationOptions.map(opt => (
                                        <CTableHeaderCell key={opt.label} className="text-center">{opt.label}</CTableHeaderCell>
                                    ))}
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {evaluationFields.map(([field, label]) => (
                                    <CTableRow key={field}>
                                        <CTableDataCell>{label}</CTableDataCell>
                                        {evaluationOptions.map(opt => renderEvaluationCell(field, opt.value, formData, setFormData, is_manager))}
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Sugestões</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="suggestions" rows={5} value={formData.suggestions || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CRow className="text-center mb-3">
                    <CCol>
                        {is_manager ? (
                            <CButton color="secondary" onClick={() => navigate("/questionarios-satisfacao")}>Voltar</CButton>
                        ) : (
                            <CButton type="submit" color="success">Concluir Questionário</CButton>
                        )}
                    </CCol>
                </CRow>
            </CForm>
        </CContainer>
    );
};

export default SatisfactionSurvey;
